/**
 * FLX — bespoke landing tokens (Layer-3 tenant override).
 *
 * Faithful recreation of the 2026 redesign handoff
 * (`client design handoffs/FLX Website Redesign Handoff/FLX Landing.dc.html`):
 * a dark, landing-first page for FLX Creatine + Electrolytes — near-black stage,
 * orange primary with a lemon highlight, Archivo display + IBM Plex Mono labels.
 *
 * The DARK PALETTE lives as CSS variables in `flx.css` (scoped to `.flx-root`),
 * so inline styles use `var(--orange)` etc. and the `:hover` rules share the same
 * source. That keeps this public page's look OWNED here (the documented
 * agp/upmi/mhyel/socialpreneur Layer-3 exception to "no hardcoded colors"); the
 * runtime tenant theme (FLX orange, Barlow, 8px radius) drives the MEMBER PORTAL,
 * which this redesign deliberately does not touch.
 *
 * This file holds only the NON-color tokens: fonts, media/asset paths, and the
 * static content the landing renders (all copy verbatim from the handoff).
 */

// ── Fonts ─────────────────────────────────────────────────────────────────────
// Loaded via TENANT_PRESETS.flx.theme.fontLinks — gen-tenant-theme.ts bakes the
// <link> into index.html at BUILD time. Run gen-tenant-theme before `dev` or
// these silently fall back to system fonts. These are the live site's own two
// families, taken from its <head>; do not swap them for a recreation's guess.
export const FONT_DISPLAY = "'Barlow Condensed', 'Barlow', sans-serif"; // headlines, kickers, nav, buttons
export const FONT_BODY = "'Barlow', system-ui, sans-serif"; // body copy
export const FONT_MONO = "ui-monospace, 'JetBrains Mono', Menlo, monospace"; // the one mono accent

/**
 * Public media base — assets are committed under `apps/member/public/tenants/flx/`
 * and shipped with the app, so they work on the box with no extra hosting.
 * `VITE_MEDIA_URL` is an optional build-time override if they ever move to an R2
 * public bucket / CDN.
 */
export const MEDIA =
  (import.meta.env.VITE_MEDIA_URL as string | undefined)?.replace(/\/+$/, '') || '/tenants/flx';

/**
 * Hero background film. Muted + looped, and skipped entirely under
 * reduced-motion / save-data.
 *
 * Re-encoded from the client's 22.6MB master (720p50, ~9.9 Mbps video + a stereo
 * audio track on a permanently muted decorative loop) down to 1.7MB: audio
 * dropped, 50 -> 30fps, CRF 28 capped at 1.8 Mbps, +faststart so it streams
 * immediately. At 28% opacity under a gradient the two are indistinguishable.
 *
 * If it is ever replaced, re-encode the same way — a 20MB background loop is
 * most of the page weight and none of the design.
 */
export const HERO_VIDEO = `${MEDIA}/hero-video.mp4`;

/**
 * Asset slots (handoff §Assets), self-hosted from the client's own site.
 *
 * Converted to WebP (2.3MB of source PNG/JPEG -> 441KB) because they are all
 * either flat brand art or product renders that survive q90 without a visible
 * change; the logo is LOSSLESS WebP. `og-image` stays JPEG — social scrapers
 * have never been reliable on WebP and it is only 23KB.
 *
 * Every one of these is rendered through `ImageSlot`, which falls back to a
 * styled placeholder frame if a file is ever missing.
 */
export const IMG = {
  logo: `${MEDIA}/flx-logo-small.webp`,
  /** The video's own first frame — no jump when playback starts. */
  heroPoster: `${MEDIA}/hero-poster.webp`,
  pouchOrange: `${MEDIA}/hero-pouch-orange.webp`,
  pouchLemon: `${MEDIA}/hero-pouch-lemon.webp`,
  stickOrange: `${MEDIA}/stick-orange.webp`,
  stickLemon: `${MEDIA}/stick-lemon.webp`,
  coach: `${MEDIA}/coach-p1.webp`,
  /** Social card only — kept JPEG because scrapers are unreliable on WebP. */
  og: `${MEDIA}/og-image.jpg`,
};

// ── Header ────────────────────────────────────────────────────────────────────
export const BRAND_TAG = 'Human Energy Optimization · MNL';

export const NAV: { label: string; href: string }[] = [
  { label: 'Why It Works', href: '#science' },
  { label: 'Inside', href: '#inside' },
  { label: "Who It's For", href: '#who' },
  { label: 'How to Use', href: '#ritual' },
  { label: 'Trust', href: '#trust' },
  { label: 'Coaches', href: '#coaches' },
  { label: 'Ecosystem', href: '#ecosystem' },
  { label: 'FAQ', href: '#faq' },
];

// ── Marquee band phrases (rendered twice for the seamless CSS loop) ───────────
export const MARQUEE: string[] = [
  'Fueling Mission-Driven Lives',
  'Be The Energy',
  'Creatine + Electrolytes',
  'Serve Your World',
  'Human Energy Optimization',
  'Strength You Can Sustain',
];

// ── Hero ──────────────────────────────────────────────────────────────────────
export const HERO = {
  kicker: 'Human Energy Optimization',
  headline: ['Fueling', 'Mission-Driven', 'Lives.'] as const,
  body: 'Strength you can sustain. Hydration you can feel. Energy for the mission you carry. Daily creatine + hydration support for strength, training, and active days.',
  primaryCta: { label: 'Join Early Access', href: '#early-access' },
  ghostCta: { label: 'Why It Works', href: '#science' },
  scroll: 'Scroll to explore',
};

/** Corner stats framing the hero. `sup` renders smaller + dimmed, as on the live site. */
export const HERO_STATS: { v: string; sup?: string; k: string }[] = [
  { v: '5g', k: 'Clinical Dose' },
  { v: '700', sup: '+', k: 'Studies Backing It' },
  { v: '0g', k: 'Added Sugar · No Dye' },
  { v: '15', k: 'Sachets Per Pouch' },
];

// ── 01 Why Creatine + Electrolytes ───────────────────────────────────────────
export const SCIENCE = {
  kicker: '01 / Why Creatine + Electrolytes',
  title: ['The most studied', 'molecule in sport.'] as const,
  lede: "Creatine isn't a trend. It's one of the most-researched performance compounds on earth — and 5g a day is the dose the science keeps landing on.",
  body: 'Your muscles and brain run on a phosphate battery called ATP. Creatine supports that energy system — helping support strength, physical performance, and training consistency. Electrolytes help replenish what active days take out.',
  /** Rendered "Not a stimulant. / A <accent>recharge.</accent>" */
  callout: ['Not a stimulant.', 'A', 'recharge.'] as const,
  calloutBody:
    'FLX skips the loading-phase folklore. A flat 5g daily supports steady saturation over three to four weeks. No cycling. No mega-scoops. Just the dose, every day — as part of your daily energy routine.',
};

/** `sup` renders in a <small> beside the figure, as on the live site. */
export const BIGNUMS: { v: string; sup: string; k: string; d: string }[] = [
  {
    v: '700',
    sup: '+',
    k: 'Peer-Reviewed Studies',
    d: 'Decades of human trials on creatine across strength and performance.',
  },
  {
    v: '5',
    sup: 'g',
    k: 'The Daily Dose',
    d: 'The clinically studied amount — no more, no guessing.',
  },
  {
    v: '3–4',
    sup: 'wk',
    k: 'To Steady Saturation',
    d: 'Consistent daily intake — no loading protocol required.',
  },
  {
    v: '100',
    sup: '%',
    k: 'Creatine Monohydrate',
    d: 'The most researched form of creatine — milled to a fine 200 mesh.',
  },
];

// ── 02 What's Inside ─────────────────────────────────────────────────────────
export const INSIDE = {
  kicker: "02 / What's Inside",
  title: ['One sachet.', 'Nothing hidden.'] as const,
  lede: 'Every milligram on the label earns its place. Open one FLX sachet and this is all you’ll find.',
  shelfKicker: 'Available at Launch',
  shelfTitle: 'Two ways to fuel',
  shelfSub: 'FLX Creatine + Electrolytes · 200 Mesh Creatine Monohydrate · 15 sachets per pouch',
  moreFlavours: {
    label: 'More Flavours In Development',
    body: 'Calamansi Salt, Blue Raspberry, Ice Tea and more are on the way. Lemon and Orange lead the launch — additional flavours release as they meet our product-trust standards.',
  },
  dosedTitle: ['Clinically dosed.', 'Nothing hidden.'] as const,
  dosedBody:
    'Both launch flavours share the same zero-sugar formula: a clinical 5g creatine dose plus a full electrolyte trio — sodium, potassium, and magnesium. Every ingredient declared; every batch lab-tested.',
  doh: 'Mahalagang Paalala: Ang FLX Creatine Monohydrate + Electrolytes ay hindi gamot at hindi dapat gamiting panggamot sa anumang uri ng sakit.',
};

/**
 * A flavour's REGULATORY LABEL — nutrition rows, ingredients, the PDRI footnote,
 * the accent colour and the fallback pouch art.
 *
 * Deliberately NOT admin-editable. Everything that actually moves (name, price,
 * compare-at, stock, product photo) comes from the CATALOG via
 * `useLandingPackages` / `useCatalog`, so the admin creates and prices the two
 * products himself. What's left here is the printed foil: it changes when the
 * artwork changes, it carries regulatory weight, and it belongs in reviewed code
 * beside the rest of the FLX copy rather than behind a CMS built for two SKUs.
 *
 * Matched to a catalog row by SKU first, then by position in the curated list
 * (Landing Packages preserves the admin's order), then not at all — an unmatched
 * product still renders as a plain card rather than disappearing.
 */
export type FlavourPreset = {
  sku: string;
  flavour: string;
  tagline: string;
  /** Per-flavour accent, fed to the `--fx` custom property. From the live site. */
  hex: string;
  /** Short caption under the hero pouch ("Juicy Orange"). */
  heroCaption: string;
  pouch: string;
  stick: string;
  serving: string;
  /** `indent` = a sub-line of the row above; `strong` = electrolyte; `cre` = the creatine row. */
  nutrition: { label: string; value: string; indent?: boolean; strong?: boolean; cre?: boolean }[];
  ingredients: string;
  footnote: string;
};

const NUTRITION_SHARED: FlavourPreset['nutrition'] = [
  { label: 'Calories', value: '5 kcal' },
  { label: 'Total Fat', value: '0 g' },
  { label: 'Saturated Fat', value: '0 g', indent: true },
  { label: 'Trans Fat', value: '0 g', indent: true },
  { label: 'Cholesterol', value: '0 mg' },
  { label: 'Sodium', value: '530 mg · 27%', strong: true },
  { label: 'Potassium', value: '125 mg · 6%', strong: true },
  { label: 'Magnesium', value: '50 mg · 21%', strong: true },
  { label: 'Total Carbohydrates', value: '2 g' },
  { label: 'Dietary Fiber', value: '0 g', indent: true },
  { label: 'Sugars', value: '0 g', indent: true },
  { label: 'Total Protein', value: '0 g' },
  { label: 'Creatine Monohydrate', value: '5 g', cre: true },
];

const PDRI_FOOTNOTE =
  '%RE/RNI based on 2015 PDRI adult (male 19–29). Sodium % per WHO 2,000 mg/day. No approved therapeutic claims.';

/** Launch order: orange first, lemon second — mirrors the curated Landing Packages order. */
export const FLAVOUR_ORDER: FlavourPreset[] = [
  {
    sku: 'FLX-CE-ORG-15',
    flavour: 'Orange',
    tagline: 'Juicy orange · zero added sugar',
    hex: '#F25C05',
    heroCaption: 'Juicy Orange',
    pouch: IMG.pouchOrange,
    stick: IMG.stickOrange,
    serving: 'Serving Size 1 stick (10 g) · 15 servings per pouch',
    nutrition: NUTRITION_SHARED,
    ingredients:
      'Creatine Monohydrate (200 Mesh) 5.000 g, Allulose 1.300 g, Sodium Chloride (Sea Salt) 1.000 g, Sodium Citrate 0.600 g, Natural Orange Peel Extract & Orange Juice Powder 0.600 g, Monk Fruit Extract 0.520 g, Magnesium Lactate Dihydrate 0.500 g, Potassium Citrate 0.345 g (provides 125 mg Potassium), Silicon Dioxide 0.100 g, Steviol Glycosides (Reb M) 0.050 g.',
    footnote: PDRI_FOOTNOTE,
  },
  {
    sku: 'FLX-CE-LEM-15',
    flavour: 'Lemon',
    tagline: 'Zesty citrus · zero added sugar',
    hex: '#E0B200',
    heroCaption: 'Zesty Citrus',
    pouch: IMG.pouchLemon,
    stick: IMG.stickLemon,
    serving: 'Serving Size 1 stick (10 g) · 15 servings per pouch',
    nutrition: NUTRITION_SHARED,
    ingredients:
      'Creatine Monohydrate (200 Mesh) 5.000 g, Allulose 1.320 g, Sodium Chloride (Sea Salt) 1.000 g, Sodium Citrate 0.600 g, Natural Lemon Peel Extract & Lemon Juice Powder 0.600 g, Monk Fruit Extract 0.500 g, Magnesium Lactate Dihydrate 0.500 g, Potassium Citrate 0.345 g (provides 125 mg Potassium), Silicon Dioxide 0.100 g, Steviol Glycosides (Reb M) 0.050 g.',
    footnote: PDRI_FOOTNOTE,
  },
];

/** SKU → preset, uppercased so an admin's lower-case SKU still matches. */
export const FLAVOURS: Record<string, FlavourPreset> = Object.fromEntries(
  FLAVOUR_ORDER.map((f) => [f.sku.toUpperCase(), f]),
);

/** The shared "Clinically dosed" ingredient ledger. */
export const INGREDIENTS: {
  n: string;
  h: string;
  tag?: string;
  p: string;
  a: string;
  u: string;
}[] = [
  {
    n: '01',
    h: 'Creatine Monohydrate',
    tag: '200 Mesh',
    p: 'The most researched form of creatine, milled to a fine 200-mesh powder so it dissolves clean — supports strength and physical performance.',
    a: '5,000',
    u: 'MG',
  },
  {
    n: '02',
    h: 'Sodium',
    p: 'From sea salt & sodium citrate — supports hydration through training and long, active days.',
    a: '530',
    u: 'MG',
  },
  {
    n: '03',
    h: 'Potassium',
    p: 'From potassium citrate — a core electrolyte that helps replenish what active days take out.',
    a: '125',
    u: 'MG',
  },
  {
    n: '04',
    h: 'Magnesium',
    p: 'From magnesium lactate — supports the electrolyte balance active bodies rely on.',
    a: '50',
    u: 'MG',
  },
  {
    n: '05',
    h: 'Natural Flavour + Sweeteners',
    p: 'Real fruit flavour with allulose, monk fruit & stevia (Reb M) — plus silicon dioxide to keep it free-flowing. No artificial dye, no added sugar, 5 kcal per sachet.',
    a: '0',
    u: 'ADDED SUGAR',
  },
];

export const FREEOF: string[] = [
  'Added Sugar',
  'Artificial Dye',
  'Bulking Fillers',
  'Loading Phase',
  'Proprietary Blends',
];

export const COA = {
  h: 'Every Batch · Certificate of Analysis',
  p: 'Lab-tested every batch. Heavy metals — lead, arsenic, cadmium, and mercury — none detected. Pathogens including E. coli, Salmonella, and S. aureus absent. Released only when it meets spec, with a 2-year shelf life.',
};

export const USE_SAFETY = {
  h: 'Use & Safety',
  directions:
    'Tear open one sachet, empty into 500 mL cold or room-temperature water. Stir until dissolved. Take one serving daily.',
  caution:
    'For adult use only. Do not exceed the recommended daily use. Not recommended for persons below 18 years old, pregnant or lactating women, or individuals with impaired kidney function. Consult a physician before use if you have a medical condition, are taking medication, or are under medical supervision. Discontinue use if adverse reactions occur. Keep out of reach of children.',
};

// ── 03 Who It's For ──────────────────────────────────────────────────────────
export const WHO = {
  kicker: "03 / Who It's For",
  title: ['Built for people', 'on a mission.'] as const,
  lede: 'If you carry responsibility — training, family, work, leadership, service — FLX is built for the energy that mission demands.',
  callout: {
    label: 'Future Energy Lines · In Development',
    body: 'FLX Creatine + Electrolytes is our flagship and current focus — the front door to a wider Human Energy Optimization ecosystem. Additional energy-support lines are in development and will be announced when they meet our Product Trust standards.',
  },
};

export const AUDIENCES: { n: string; h: string; p: string; chips: string[] }[] = [
  {
    n: '01',
    h: 'Fitness Coaches',
    p: 'You train others all day and carry your own standards too. FLX supports the strength and hydration routines you already teach — in a format you can explain in one sentence.',
    chips: ['Training Consistency', 'Coach-Friendly Education'],
  },
  {
    n: '02',
    h: 'Active Professionals',
    p: 'Long hours, training before or after work, family in between. One sachet supports your strength and daily energy routine without adding complexity to the day.',
    chips: ['Daily Energy Routine', 'Zero Guesswork'],
  },
  {
    n: '03',
    h: 'Mission-Driven Builders',
    p: 'Leaders, parents, servants, entrepreneurs. Energy is the capacity to show up consistently for what matters — and strength is stewardship, not vanity.',
    chips: ['Show Up Stronger', 'Discipline Over Motivation'],
  },
];

// ── 04 The Daily Ritual ──────────────────────────────────────────────────────
export const RITUAL = {
  kicker: '04 / The Daily Ritual',
  title: ['Tear, pour,', 'shake, repeat.'] as const,
  lede: 'Mix one 10g sachet with 500ml of cold water. Shake well. Use before, during, or after training — or as part of your daily hydration routine.',
};

export const STEPS: { n: string; meta: string; h: string; p: string }[] = [
  {
    n: '01',
    meta: '5g',
    h: 'Tear',
    p: 'Snap the notch on the upper left. Opens clean — no scissors required.',
  },
  {
    n: '02',
    meta: '500ML',
    h: 'Pour',
    p: 'Empty one 10g sachet into 500ml of cold water. One sachet, one serving.',
  },
  {
    n: '03',
    meta: 'FLX',
    h: 'Shake',
    p: 'Five seconds. Dissolves clean — no clumps, no chalky aftertaste.',
  },
  {
    n: '04',
    meta: 'DAY 30 STREAK',
    h: 'Repeat',
    p: 'Daily, every day. Consistency beats intensity — same sachet, same ritual, same mission.',
  },
];

// ── 05 Our Trust Standards ───────────────────────────────────────────────────
export const TRUST = {
  kicker: '05 / Our Trust Standards',
  title: ['Trust is the', 'whole strategy.'] as const,
  lede: "FLX earns belief through credible ingredients, honest labels, and responsible claims — not hype. Here's what we hold ourselves to.",
  callout: {
    label: 'Early Product Testing In Progress',
    body: 'We are currently gathering feedback from fitness coaches, active professionals, and early product testers. Real reviews and testimonials will be published here — with permission — as our taste-test and feedback program completes.',
    cta: { label: 'Become an Early Tester →', href: '#early-access' },
  },
  certKicker: 'Manufactured to Standard',
  certTitle: ['Certified &', 'compliant.'] as const, // rendered on one line
  certLede:
    'Produced in facilities holding internationally recognised food-safety and quality-management certifications.',
};

export const STANDARDS: { n: string; h: string; p: string }[] = [
  {
    n: '01',
    h: 'Clinically Studied Ingredients',
    p: 'Creatine monohydrate is one of the most researched compounds in sports science. We use the studied dose — 5g daily — nothing exotic, nothing unproven.',
  },
  {
    n: '02',
    h: 'Transparent, Tested Label',
    p: "Every milligram declared — no proprietary blends, no artificial dye, no added sugar. And every batch is backed by a Certificate of Analysis: heavy metals none detected, pathogens absent. What's on the label is what's in the sachet.",
  },
  {
    n: '03',
    h: 'Responsible Claims Only',
    p: 'We say "supports strength" and "helps replenish electrolytes" — never "cures," "treats," or "guarantees." This is a food supplement, not a medicine, and we label it that way.',
  },
  {
    n: '04',
    h: 'Coach-Led Education',
    p: 'Product education comes from fitness professionals who understand training — explained simply, honestly, and without pressure.',
  },
];

export const CERTS: { src: string; alt: string; caption: string }[] = [
  { src: `${MEDIA}/cert-haccp.webp`, alt: 'HACCP certified', caption: 'Food Safety' },
  { src: `${MEDIA}/cert-iso9001.webp`, alt: 'ISO 9001 certified', caption: 'Quality Management' },
  {
    src: `${MEDIA}/cert-gmp.webp`,
    alt: 'GMP — Good Manufacturing Practice certified',
    caption: 'Good Mfg. Practice',
  },
  { src: `${MEDIA}/cert-iso22000.webp`, alt: 'ISO 22000 certified', caption: 'Food Safety Mgmt.' },
];

// ── Full-bleed banner ────────────────────────────────────────────────────────
export const BANNER = {
  tag: '#BeTheEnergy',
  title: ['Become the coach.', 'Lead the mission.'] as const,
  body: 'FLX begins with people who serve their world. Use it, believe in it, and become the coach and leader others draw strength from.',
  cta: { label: 'Apply as a Coach Pioneer →', href: '#early-access' },
};

// ── 06 Coach-Led Culture ─────────────────────────────────────────────────────
export const COACHES = {
  kicker: '06 / Coach-Led Culture',
  title: ['Coach-led', 'product education.'] as const,
  lede: 'Coaches are not salespeople here. They are educators and culture carriers — the people who already teach discipline, consistency, and transformation. Your sphere of influence is not a market to exploit. It is a world you have an opportunity to serve.',
  callout: {
    label: 'FLX Coach Pioneer Program',
    body: "We're inviting a founding group of Filipino fitness coaches to test the product, shape the education materials, and help build the culture from day one.",
    cta: { label: 'Apply as a Coach Pioneer →', href: '#early-access' },
  },
};

export const CULTURE_OLD = {
  eyebrow: 'The Coach Reality',
  title: 'Beyond Sessions',
  lead: "Coaches carry other people's transformation every day — while their own time, energy, and income stay tied to the session hour.",
  points: [
    'Income limited by hours on the gym floor',
    'Influence and trust that stop at the session',
    'Clients asking "what should I take?" with no credible answer to point to',
    'No simple system to extend impact beyond training hours',
  ],
};

export const CULTURE_FLX = {
  eyebrow: 'The FLX Pathway',
  title: 'Serve Your World',
  lead: 'You already serve a world. FLX helps you serve it with better tools, build genuine customer value, and develop systems beyond the session hour — without turning the people you care about into prospects.',
  points: [
    'Simple, honest product education you can teach',
    'A credible answer built on responsible claims',
    'Community and culture built on discipline, not hype',
    'A builder pathway to Be The Energy and grow beyond sessions',
  ],
};

// ── Photo band ───────────────────────────────────────────────────────────────
export const PHOTO_BAND = {
  tag: '#BeTheEnergy',
  title: ['Be the energy', 'in the room.'] as const,
  body: 'Real people fueling real missions — becoming a source of strength, discipline, and trust for the world around them.',
  meta: 'Energy · Leverage · Discipline · Impact',
};

// ── 07 The FLX Ecosystem ─────────────────────────────────────────────────────
export const ECOSYSTEM = {
  kicker: '07 / The FLX Ecosystem',
  title: ['FLX is an', 'ecosystem.'] as const,
  lede: 'More than a supplement brand — an ecosystem for fueling mission-driven lives through Human Energy Optimization. The product is where most people meet us; the ecosystem is the full value: service, culture, and a development path for those who want it.',
  callout: {
    label: 'Serve → Build + Become → Steward',
    body: 'The product must stand on its own value; the ecosystem adds value around it. Business and leadership pathways are introduced responsibly, in their own time — never as the first thing, and never as a promise of income.',
  },
};

export const ECOSYSTEM_CARDS: { n: string; h: string; p: string }[] = [
  {
    n: 'Serve',
    h: 'Serve Your World',
    p: 'It starts with service. Be The Energy — become a source of strength in the world you already influence — and let genuine product value build trust before anything else.',
  },
  {
    n: 'Build',
    h: 'Build + Become',
    p: 'For those who go further, two roads run together: BUILD develops real economic capacity through customers and systems; BECOME develops the character to carry it. Capacity should not grow faster than character.',
  },
  {
    n: 'Steward',
    h: 'Multiply The Impact',
    p: "The goal isn't rank or hype. It's stewardship — responsible people who strengthen others and multiply mission impact, never dependency.",
  },
];

// ── 08 Get Involved (early access) ───────────────────────────────────────────
export const EARLY_ACCESS = {
  kicker: '08 / Get Involved',
  title: ['Be part of it', 'before launch.'] as const,
  lede: 'FLX Creatine + Electrolytes is in its final pre-launch stage. Three ways to get involved early.',
  pitch: ['The mission is', 'fuel.', 'The launch is', 'coming.'] as const,
  pitchBody:
    'One sachet per day. Creatine + electrolytes. Built for the mission you carry. Join the list and be first to try it.',
  bullets: [
    '10g sachet · 15 sachets per pouch',
    '5g creatine (200 mesh) + sodium, potassium & magnesium per sachet',
    'Zero added sugar · 5 kcal · no artificial dye',
  ],
  formLabel: "I'm here for",
  submit: 'Count Me In',
  microcopy: "No spam. We'll only reach out about the FLX launch and your chosen pathway.",
  success: {
    title: "You're on the list.",
    again: 'Submit Another',
  },
};

/** Per-pathway confirmation copy, verbatim from the live site's `MESSAGES` map. */
export const PATHWAY_SUCCESS: Record<string, string> = {
  'Early Access':
    "You're on the early access list. You'll be first to know the moment FLX Creatine + Electrolytes launches.",
  'Coach Pioneer':
    "Your Coach Pioneer application is in. We'll reach out about testing the product and shaping the culture.",
  'Feedback Session':
    "Noted — we'd love to run a taste-test with your community. We'll be in touch to set it up.",
};

/** The three pathways. `value` is what rides into the feedback subject line. */
export const PATHWAYS: { n: string; value: string; sub: string }[] = [
  { n: '01', value: 'Early Access', sub: 'Be first to try it' },
  { n: '02', value: 'Coach Pioneer', sub: 'Build the culture' },
  { n: '03', value: 'Feedback Session', sub: 'Host a taste-test' },
];

// ── FAQ ──────────────────────────────────────────────────────────────────────
export const FAQ_HEAD = {
  kicker: '08 / Common Questions',
  title: ['The only dumb', 'question is none.'] as const,
  lede: "Everything you'd want to ask a sports dietitian about creatine, answered directly.",
};

export const FAQS: { q: string; a: string }[] = [
  {
    q: 'Do I need to load creatine?',
    a: 'No. A consistent 5g daily dose supports steady saturation over three to four weeks — the same place a loading protocol ends up. FLX is designed around the simple, sustainable routine: one sachet, every day.',
  },
  {
    q: 'When should I take FLX?',
    a: "Consistency matters more than timing. Use it before, during, or after training — or simply as part of your daily hydration routine. Pick a time you'll actually stick to.",
  },
  {
    q: 'What exactly is in one sachet?',
    a: "Each 10g sachet contains 5g of creatine monohydrate — the most researched form of creatine, milled to a fine 200 mesh so it dissolves cleanly without grit — plus a full electrolyte trio: sodium (530mg), potassium (125mg), and magnesium (50mg). It's flavoured with real fruit and sweetened with allulose, monk fruit, and stevia — no added sugar, no artificial dye, just 5 kcal. Every ingredient is declared on the label, there are no proprietary blends, and every batch is lab-tested for heavy metals and microbial safety.",
  },
  {
    q: 'Is creatine only for bodybuilders?',
    a: 'No. Creatine is one of the most widely studied supplements for anyone who trains or lives an active life — coaches, professionals, and everyday athletes. FLX is built for people who carry a mission, not just a gym membership.',
  },
  {
    q: 'Is FLX a medicine?',
    a: 'No. FLX Creatine + Electrolytes is a food supplement, not a medicine, and it is not intended to diagnose, treat, cure, or prevent any disease. It supports strength, hydration, and training consistency as part of a disciplined, active lifestyle.',
  },
  {
    q: 'When can I buy it?',
    a: "FLX Creatine + Electrolytes is in its final pre-launch stage, with early product testing in progress. Join the Early Access List and you'll be first to know when it's available — and you may be invited to taste-test before launch.",
  },
];

// ── Footer ───────────────────────────────────────────────────────────────────
export const FOOTER_BLURB =
  'Fueling Mission-Driven Lives. FLX is an ecosystem for fueling mission-driven lives through Human Energy Optimization — credible products, coach-led culture, and a responsible development path for people on a mission.';

export const FOOTER_COMPANY = {
  name: 'FLX Global Marketing, Inc.',
  address:
    '28, 9/F, Intellectual Property Center, Upper McKinley Rd., Pinagsama, Taguig City, Philippines',
};

export const FOOTER: { h: string; links: { label: string; href: string }[] }[] = [
  {
    h: 'Product',
    links: [
      { label: 'FLX Creatine + Electrolytes', href: '#inside' },
      { label: "Who It's For", href: '#who' },
      { label: 'Trust Standards', href: '#trust' },
      { label: 'Early Access', href: '#early-access' },
    ],
  },
  {
    h: 'Learn',
    links: [
      { label: 'Why Creatine + Electrolytes', href: '#science' },
      { label: "What's Inside", href: '#inside' },
      { label: 'How to Use', href: '#ritual' },
      { label: 'Coach-Led Education', href: '#coaches' },
    ],
  },
  {
    h: 'Company',
    links: [
      { label: 'About FLX', href: '#ecosystem' },
      { label: 'Coach Pioneer Program', href: '#early-access' },
      { label: 'Feedback Sessions', href: '#early-access' },
      { label: 'Contact', href: '#early-access' },
    ],
  },
];

export const FOOTER_LEGAL = [
  '© 2026 FLX Global Marketing, Inc. · Distributed exclusively in the Philippines · FLXLIFESTYLE.COM',
  'Food supplement. No approved therapeutic claims. Not intended to diagnose, treat, cure, or prevent any disease.',
];
